/* eslint-disable max-statements */
import React, { useEffect, useState } from 'react';
import { Spin, Row, Col, Button } from 'antd';
import { isEmpty } from 'lodash';

import I18nCustomFormatter from 'Src/common/components/i18nCustomFormatter';
import history from 'Src/common/utilities/history';
import useDebounce from 'Src/common/hooks/useDebounce';
import { MENTOR_MODAL_PARAM } from 'Src/mentorship/constants';
import { getMentors, getOnboardingStatus, getConnectionReport } from 'Src/mentorship/actions';
import Wrapper from 'Src/mentorship/components/mentors/home/wrapper';
import SearchAndFilters from 'Src/mentorship/components/mentors/home/searchAndFilter';
import MentorCard from 'Src/mentorship/components/mentors/home/mentorCard';
import NoResultsFound from 'Src/mentorship/components/mentors/home/noResultsFound';
import RequestSentModal from 'Src/mentorship/components/mentors/requestSentModal';
import NoMentorsFound from 'Src/mentorship/components/mentors/home/noMentorsFound';
import MentorDetails from './mentorDetails';
import FiltersModal from './filtersModal';
import { addOrRemoveParams } from '../../../utils';

function Mentors() {
  const [mentorsData, setMentorsData] = useState([]);
  const [onboardingStatus, setOnboardingStatus] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [isMentorDetailsModalOpen, setIsMentorDetailsModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isRequestSentModalOpen, setIsRequestSentModalOpen] = useState(false);
  const [modalData, setModalData] = useState({});
  const [connectionData, setConnectionData] = useState({});
  const [filters, setFilters] = useState({});
  const debouncedQuery = useDebounce(search, 500);

  const getMentorsData = (data = filters, page = currentPage) => {
    setLoading(true);
    getMentors(page, debouncedQuery, data)
      .then((res) => {
        setMentorsData(res.data);
        setHasMore(!!res.data.next);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (params.has(MENTOR_MODAL_PARAM)) {
      const id = params.get(MENTOR_MODAL_PARAM);
      setIsMentorDetailsModalOpen(true);
      setModalData({ id });
    }

    const apis = [getOnboardingStatus(), getConnectionReport()];
    setPageLoading(true);

    Promise.all(apis)
      .then((response) => {
        const [onBoardingRes, connectionReportRes] = response;

        if (!onBoardingRes.data.is_completed) {
          history.push('/mentorship/onboarding');
        } else {
          setOnboardingStatus(onBoardingRes.data);
        }

        setConnectionData(connectionReportRes.data);
        setPageLoading(false);
      })
      .catch(() => {
        setPageLoading(false);
      });
  }, []);

  useEffect(() => {
    getMentorsData();
  }, [debouncedQuery]);

  const handleSearch = (event) => {
    setSearch(event.target.value);
    setCurrentPage(1);
  };

  const handleLoadMore = () => {
    setCurrentPage((prev) => prev + 1);
    setButtonLoading(true);

    getMentors(currentPage + 1, debouncedQuery, filters)
      .then((res) => {
        const newResults = [...mentorsData.results, ...res.data.results];
        setMentorsData({ ...res.data, results: newResults });
        setHasMore(!!res.data.next);
        setButtonLoading(false);
      })
      .catch(() => {
        setButtonLoading(false);
      });
  };

  const handleViewDetailsClick = (data) => {
    setModalData(data);
    setIsMentorDetailsModalOpen(true);
    addOrRemoveParams({ key: MENTOR_MODAL_PARAM, value: data.id, action: 'add' });
  };

  const closeMentorDetailsModal = () => {
    setIsMentorDetailsModalOpen(false);
    setModalData(false);
    addOrRemoveParams({ key: MENTOR_MODAL_PARAM, action: 'remove' });
  };

  const toggleFilterModal = () => {
    setIsFilterModalOpen((prev) => !prev);
  };

  const toggleRequestSentModal = () => {
    setIsRequestSentModalOpen((prev) => !prev);
  };

  const getFilteredMentors = (data) => {
    getMentorsData(data, 1);
    setCurrentPage(1);
    setFilters(data);
  };

  if (pageLoading) {
    return <Spin />;
  }

  if (!loading && isEmpty(mentorsData.results) && !debouncedQuery && isEmpty(filters)) {
    return <NoMentorsFound />;
  }

  return (
    <Wrapper>
      <SearchAndFilters handleSearch={handleSearch} toggleFilterModal={toggleFilterModal} />
      <FiltersModal
        isOpen={isFilterModalOpen}
        toggleModal={toggleFilterModal}
        getFilteredMentors={getFilteredMentors} />
      <Choose>
        <When condition={loading}>
          <Spin />
        </When>
        <Otherwise>
          <Choose>
            <When condition={(debouncedQuery || !isEmpty(filters)) && isEmpty(mentorsData.results)}>
              <NoResultsFound />
            </When>
            <Otherwise>
              <Row gutter={[16, 16]}>
                {mentorsData?.results?.map((mentor) => (
                  <Col xs={24} lg={12} key={mentor.id}>
                    <MentorCard data={mentor} handleViewDetailsClick={handleViewDetailsClick} />
                  </Col>
                ))}
              </Row>
              <If condition={hasMore}>
                <Button
                  type="primary"
                  loading={buttonLoading}
                  onClick={handleLoadMore}
                  className="mentors-load-more-btn">
                  <I18nCustomFormatter id="load-more-button" />
                </Button>
              </If>
              <If condition={isMentorDetailsModalOpen}>
                <MentorDetails
                  isOpen={isMentorDetailsModalOpen}
                  closeMentorDetailsModal={closeMentorDetailsModal}
                  modalData={modalData}
                  toggleRequestSentModal={toggleRequestSentModal}
                  onboardingStatus={onboardingStatus}
                  connectionData={connectionData}
                  setConnectionData={setConnectionData} />
              </If>
              <If condition={isRequestSentModalOpen}>
                <RequestSentModal toggleModal={toggleRequestSentModal} connectionData={connectionData} />
              </If>
            </Otherwise>
          </Choose>
        </Otherwise>
      </Choose>
    </Wrapper>
  );
}

export default Mentors;
